import React, { useEffect, useState } from "react";

/**
 * @typedef {"newwindow" | "redirect"} OpenOptions
 *
 * @typedef {Object} BookVisitConfig
 * @property {String} channelId - The channel ID for the booking widget (UUID).
 * @property {'Standard2Columns'} widgetLayout
 * @property {String} WidgetContainerId - The ID of the widget container eg <div id="widgetContainer"></div>.
 * @property {Boolean} promoCode - Whether to show the promo code field.
 * @property {Boolean} corpCode - Whether to show the corporate code field.
 * @property {Boolean} agentCode - Whether to show the agent code field.
 * @property {OpenOptions} openOption - The option for opening the booking widget
 * @property {Boolean} loginLink - Whether to show the login link.
 * @property {String} currency - The currency code (e.g., "SEK").
 * @property {String} languageCulture - The language culture code (e.g., "nb").
 * @property {String} type - The type of booking widget (e.g., "single_property").
 *
 */

/**
 * A component that renders a booking widget for accommodations.
 *
 * @param {Object} props - The component props.
 * @param {BookVisitConfig} props.config - Configuration object for the booking widget.
 *
 * @returns {JSX.Element} The rendered booking widget component.
 */
export const BookVisitWidget = ({ config }) => {
  const loaded = useScript("https://reservations.bookvisit.com/widget?type=accommodation");

  useEffect(() => {
    if (loaded) {
      BV.SPWidget({
        channelId: config.channelId,
        widgetLayout: config.widgetLayout,
        WidgetContainerId: config.WidgetContainerId,
        promoCode: config.promoCode,
        corpCode: config.corpCode,
        agentCode: config.agentCode,
        openOption: config.openOption,
        loginLink: config.loginLink,
        currency: config.currency,
        languageCulture: config.languageCulture,
        type: config.type,
        accommodationTypePicker: config.accommodationTypePicker,
      });
    }
  }, [loaded]);

  return <div id={config.WidgetContainerId} />;
};

/**
 * Custom hook to load a script dynamically.
 *
 * @param {string} url - The URL of the script to load.
 * @returns {boolean} - A boolean indicating whether the script has been loaded.
 */
const useScript = (url) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = url;
    script.type = "text/javascript";
    script.async = true;
    script.onload = () => setLoaded(true);
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [url]);

  return loaded;
};
